import React, { useState, useEffect } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "../../shared/common/ContentHeaderSection";
import apiCall from "shared/services/ApiAdapter";

const BatchAssignmentsContent = ({
  navigateTo,
  selectedBatchId,
  selectedCourseTitle,
  useMockData,
  displayToast,
}) => {
  const [assignments, setAssignments] = useState([]);
  const [batchEvaluations, setBatchEvaluations] = useState([]);
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!selectedBatchId) return;

      try {
        // Get assignments for this batch using the dedicated endpoint
        const batchAssignments = await apiCall(
          `/batch-assignments/batch/${selectedBatchId}/assignments`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setAssignments(batchAssignments || []);

        // Get unique evaluations assigned to this batch
        const batchEvaluations = await apiCall(
          `/batch-assignments/batch/${selectedBatchId}/evaluations`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setBatchEvaluations(batchEvaluations || []);
      } catch (error) {
        console.error("Failed to fetch batch assignments:", error);
        displayToast("Failed to fetch assignments", "error");
      }
    };

    fetchAssignments();
  }, [selectedBatchId, useMockData, startLoading, stopLoading, displayToast]);

  const getStatusColor = (assignment) => {
    if (assignment.submittedDateTime) {
      return "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100";
    } else if (
      assignment.expiryDateTime &&
      new Date(assignment.expiryDateTime) < new Date()
    ) {
      return "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100";
    } else {
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100";
    }
  };

  const getStatusText = (assignment) => {
    if (assignment.submittedDateTime) {
      return t("submittedAssignments");
    } else if (
      assignment.expiryDateTime &&
      new Date(assignment.expiryDateTime) < new Date()
    ) {
      return t("expiredAssignments");
    } else {
      return t("pendingAssignments");
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "N/A";
    return new Date(dateTime).toLocaleString();
  };

  const rightSideContentTop = (
    <>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
    </>
  );

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                navigateTo(
                  "batchEvaluations",
                  selectedBatchId,
                  selectedCourseTitle
                )
              }
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to batch evaluations"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {t("batchAssignments")}
            </span>
          </div>
        }
        subtitle={t("viewBatchAssignments")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={null}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("totalAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {assignments.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("submittedAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {assignments.filter((a) => a.submittedDateTime).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-yellow-100 dark:bg-yellow-900">
              <svg
                className="w-6 h-6 text-yellow-600 dark:text-yellow-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("pendingAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {
                  assignments.filter(
                    (a) =>
                      !a.submittedDateTime &&
                      (!a.expiryDateTime ||
                        new Date(a.expiryDateTime) >= new Date())
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("expiredAssignments")}
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {
                  assignments.filter(
                    (a) =>
                      a.expiryDateTime &&
                      new Date(a.expiryDateTime) < new Date() &&
                      !a.submittedDateTime
                  ).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Assignment Details
        </h3>

        {assignments.length === 0 ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            <p className="mt-2">No assignments found for this batch</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assignments.map((assignment) => {
              return (
                <div
                  key={assignment.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <img
                          src={
                            assignment.student?.profilePictureUrl ||
                            `https://placehold.co/40x40/e0f2f7/00796b?text=${assignment.student?.name?.charAt(0) || "S"
                            }`
                          }
                          alt={assignment.student?.name || "Student"}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {assignment.student?.name || "Unknown Student"}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {assignment.evaluation?.chapter ||
                              "Unknown Evaluation"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Sent: {formatDateTime(assignment.sentDateTime)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Due: {formatDateTime(assignment.expiryDateTime)}
                        </p>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          assignment
                        )}`}
                      >
                        {getStatusText(assignment)}
                      </span>

                      {assignment.score !== null && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t("assignmentScore")}
                          </p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {assignment.score}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {assignment.feedback && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>{t("assignmentFeedback")}:</strong>{" "}
                        {assignment.feedback}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unique Evaluations Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t("batchEvaluations")}
        </h3>

        {batchEvaluations.length === 0 ? (
          <div className="text-center text-gray-600 dark:text-gray-400 py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            <p className="mt-2">{t("noBatchEvaluationsFound")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {batchEvaluations.map((evaluation) => (
              <div
                key={evaluation.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() =>
                  navigateTo(
                    "evaluationAssignments",
                    selectedBatchId,
                    selectedCourseTitle,
                    evaluation.id
                  )
                }
              >
                <div className="flex items-center justify-center w-10 h-10 text-xl font-bold text-blue-700 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-300 mb-3">
                  E
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {evaluation.chapter}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {evaluation.conceptTopic || "No topic"} |{" "}
                  {evaluation.difficulty}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {evaluation.numberOfQuestions} Qs •{" "}
                  {evaluation.maxTimeMinutes} mins
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Due: {evaluation.expiryDateTime?.split("T")[0]}
                </p>
                {evaluation.isPractice && (
                  <span className="inline-block mt-2 text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full dark:bg-green-800 dark:text-green-100">
                    {t("practice")}
                  </span>
                )}
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {t("clickToViewAssignments")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchAssignmentsContent;
